import type React from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { isInputFieldElement, OpCode, Queries } from '@aline/common';
import { debounce } from 'debounce';
import { usePrevious } from 'react-use';
import { Editor } from 'slate';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import type { PanelVariableType } from 'src/components';
import { usePDFFieldAPI, useSetVariablePanelState, useVariablePanelState } from 'src/components';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { comparePDFAnnotationPosition } from 'src/components/viewer/viewer-hooks';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  useEditorTabStateSelectorForTabId,
  useGetFieldsInJSON,
} from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { suspendSelectionScroll } from 'src/editor/transforms/scroll-transforms';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useGetIsPDFFieldInDocument, useGetPDFFieldsByNodeId } from 'src/helpers';
import { IS_MOBILE } from 'src/helpers/environment';
import {
  useAuthorizedTransforms,
  useGetCurrentDoc,
  useGetVariablesForDocument,
  useNotificationDispatch,
  useOnChange,
} from 'src/hooks';
import type { AlineClientEditor } from 'src/layout-engine';
import { EDITOR_TO_UPDATE_CONTEXT, useMaybeEditor } from 'src/layout-engine';
import { NotificationsActionType } from 'src/notifications';
import { useEditingDisabled, useEditingMode } from 'src/store/document/selectors';
import type { ReactSetStateAction, UserSubset } from 'src/types';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid4 } from 'uuid';

import { getCheckboxOptionIdentities, toCheckboxChoiceRefIds } from './checkbox-option-ids';

interface UseIsInDocumentJSONRet extends ReturnType<typeof useGetFieldsInJSON> {
  isInChildren: boolean;
}

export function useIsFieldInDocJSON(
  field: Pick<Apollo.Field, 'nodeId' | '__typename'>
): UseIsInDocumentJSONRet {
  const res = useGetFieldsInJSON();
  const { fieldIdsInChildren } = res;

  return { ...res, isInChildren: fieldIdsInChildren.has(field.nodeId) };
}

/**
 * Scroll / blur Outline controls when `focusedField` changes. Focus the Outline input only on
 * document-field clicks so arrow-key navigation keeps editor focus.
 */
export function useOnChangeFocusVariable(
  variable: PanelVariableType,
  options: {
    inputEl: HTMLInputElement | null;
    liEl: HTMLLIElement | null;
  }
): { onFocus: () => void } {
  const { inputEl, liEl } = options;
  const { scrollFieldIntoView } = useScrollFieldIntoView();
  const { focusedField } = useVariablePanelState();
  const setVariablePanelState = useSetVariablePanelState();
  const editor = useMaybeEditor();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const lastMouseDownTimestamp = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.click.lastMouseDownTimestamp
  );

  const scrolledForIdRef = useRef<string | null>(null);
  const isFocusedVariable = focusedField?.id === variable.id;

  const onFocus = useCallback(() => {
    setVariablePanelState({ focusedField: variable });
  }, [setVariablePanelState, variable]);

  useEffect(() => {
    if (!isFocusedVariable) {
      scrolledForIdRef.current = null;

      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && liEl?.contains(activeElement)) {
        activeElement.blur();
      }
      return;
    }

    if (liEl) scrollIntoView(liEl, { behavior: 'smooth', scrollMode: 'if-needed' });
    // Only scroll the field into view when focus actually moves to it.
    if (scrolledForIdRef.current !== variable.id) {
      scrolledForIdRef.current = variable.id;
      scrollFieldIntoView(variable);
    }

    if (
      !document.activeElement?.hasAttribute('data-keyboard-input-handler') &&
      !liEl?.contains(document.activeElement)
    ) {
      inputEl?.focus({ preventScroll: true });
    }
  }, [isFocusedVariable, liEl, inputEl, scrollFieldIntoView, variable]);

  useOnChange(() => {
    if (!editor || !inputEl) return;
    const fieldEntry = Editor.above(editor, { match: isInputFieldElement });
    if (!fieldEntry || fieldEntry[0].fieldId !== variable.nodeId) return;
    // Defer past ClientTransforms.focus's microtask so the editor doesn't steal focus back.
    queueMicrotask(() => inputEl.focus({ preventScroll: true }));
  }, [lastMouseDownTimestamp]);

  return { onFocus };
}

interface UpdateVariableAction extends Pick<Apollo.WorkflowAction, 'id' | '__typename'> {
  assignee: UserSubset | null;
}
interface UseUpdateVariable extends Pick<
  PanelVariableType,
  'id' | 'isField' | 'label' | 'nodeId' | '__typename' | 'value'
> {
  action: UpdateVariableAction | null;
}
type VariableWithValue = Pick<Apollo.DocumentVariable, 'nodeId' | '__typename' | 'value'>;

export function useUpdatePrimitiveVariableValue(
  variable: UseUpdateVariable,
  options: {
    isFocused: boolean;
    syncVariableValue: (variable: VariableWithValue) => void;
  }
): {
  editor: AlineClientEditor | null;
  isInChildren: boolean;
  onClickVariable: (e: React.MouseEvent) => void;
  resetOnBlur: () => OpCode;
  updatePrimitiveVariable: (args: {
    formattedValue: string | null;
    value: string | null;
  }) => Promise<void>;
} {
  const { isFocused, syncVariableValue } = options;
  const editor = useMaybeEditor();
  const { document } = useGetCurrentDoc();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const setVariablePanelState = useSetVariablePanelState();
  const { setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const prevValue = usePrevious(variable.value);
  const { isInChildren } = useIsFieldInDocJSON(variable);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const { resetOnBlur } = useResetTemplateFieldOnBlur(variable);
  const safeSyncVariableValue = useCallback(syncVariableValue, [syncVariableValue]);
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const updateVariableDebounced = useDebouncedCallback(updateVariable, 1000);
  const isContract = document?.variant === Apollo.DocumentVariant.Document;
  const isValueChanged = prevValue !== variable.value;

  useEffect(() => {
    /**
     * Variable.value can be set from outside the component (e.g., filling in a field in the document).
     * Ensure value is only set when input is not focused so value is not overridden while user is focused on input.
     */
    if (!isFocused && isValueChanged) safeSyncVariableValue(variable);
  }, [isFocused, isValueChanged, safeSyncVariableValue, variable]);

  return {
    editor,
    isInChildren,
    onClickVariable: () => {
      setVariablePanelState({ focusedField: variable });
    },
    resetOnBlur,
    updatePrimitiveVariable: async ({ formattedValue, value }) => {
      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion': {
          if (!isInChildren) {
            await updateVariableDebounced({
              variables: { input: { id: variable.id, props: { value: value || null } } },
            });
            return;
          }

          if (!currentUserId || !editingMode || !editor) return;

          const assigneeId = variable.action?.assignee?.id ?? null;
          // Use null for template so anyone can fill out/test the field...
          AuthorizedTransforms.updateInputField(editor, {
            assigneeId: isContract ? assigneeId : null,
            authorId: currentUserId,
            editingMode,
            fieldId: variable.nodeId,
            // If string is '' or null, revert to label for Aline docs so field label is present in doc.
            update: { text: formattedValue || variable.label },
          });
          break;
        }
        case 'StaticFileVersion': {
          const isInPDF = getIsPDFFieldInDoc(variable.nodeId);
          if (!isInPDF) {
            await updateVariableDebounced({
              variables: { input: { id: variable.id, props: { value: value || null } } },
            });
            return;
          }

          // Syncing annotations to DB updates field in DB.
          setPDFFieldValue(variable.nodeId, formattedValue ?? '');
          break;
        }
      }
    },
  };
}

type OptionsValue = PanelVariableType['answerOptions']['options'][number];

export function useUpdateOptionsVariableValue(
  optsVariable: PanelVariableType,
  options: { isFocused: boolean }
): {
  isInChildren: boolean;
  onClickVariable: (e: React.MouseEvent) => void;
  options: OptionsValue[];
  resetOnBlur: () => OpCode;
  selectedAnswer: PanelVariableType['selectedAnswer'] | null;
  setSelectedAnswer: ReactSetStateAction<PanelVariableType['selectedAnswer'] | null>;
  updateOptionsVariable: (value: OptionsValue | null) => Promise<void>;
} {
  const { isFocused } = options;
  const editor = useMaybeEditor();
  const dispatch = useNotificationDispatch();
  const { document } = useGetCurrentDoc();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const setVariablePanelState = useSetVariablePanelState();
  const { setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const prevValue = usePrevious(optsVariable.selectedAnswer);
  const { isInChildren } = useIsFieldInDocJSON(optsVariable);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const { resetOnBlur } = useResetTemplateFieldOnBlur(optsVariable);
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const [selectedAnswer, setSelectedAnswer] = useState<PanelVariableType['selectedAnswer']>(
    optsVariable.selectedAnswer
  );
  const optionsList = useMemo(
    () => optsVariable.answerOptions?.options ?? [],
    [optsVariable.answerOptions?.options]
  );
  const isContract = document?.variant === Apollo.DocumentVariant.Document;
  const isValueChanged = prevValue !== optsVariable.selectedAnswer;

  useEffect(() => {
    /**
     * Variable.selectedAnswer can be set from outside the component (e.g., filling in a field in the document).
     * Ensure value is only set when input is not focused so value is not overridden while user is focused on input.
     */
    if (!isFocused && isValueChanged) {
      const syncedSelectedAnswer =
        optionsList.find(o => o.id === optsVariable.selectedAnswer?.id) ?? null;
      if (selectedAnswer !== syncedSelectedAnswer) setSelectedAnswer(syncedSelectedAnswer);
    }
  }, [
    isFocused,
    optsVariable.selectedAnswer?.id,
    optionsList,
    setSelectedAnswer,
    selectedAnswer,
    isValueChanged,
  ]);

  return {
    isInChildren,
    onClickVariable: () => {
      setVariablePanelState({ focusedField: optsVariable });
    },
    options: optionsList,
    resetOnBlur,
    selectedAnswer,
    setSelectedAnswer,
    updateOptionsVariable: async value => {
      setSelectedAnswer(value);

      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion': {
          if (!isInChildren) {
            // Variable is not in JSON.  Update directly.
            await updateVariable({
              variables: {
                input: { id: optsVariable.id, props: { selectedAnswerId: value?.id ?? null } },
              },
            });
            return;
          }

          if (!currentUserId || !editingMode || !editor) return;

          const assigneeId = optsVariable.action?.assignee?.id ?? null;
          const { errMessage } = AuthorizedTransforms.switchAllDCsWithFieldId(editor, {
            assigneeId: isContract ? assigneeId : null,
            currentUserId,
            editingMode,
            fieldId: optsVariable.nodeId,
            selectedTrigger: value
              ? {
                  answerOptionChoiceId: value.choiceRefId,
                  conditionType: 'option-choice',
                  fieldId: optsVariable.nodeId,
                }
              : 'not-set',
          });
          AuthorizedTransforms.updateInputField(editor, {
            assigneeId: isContract ? assigneeId : null,
            authorId: currentUserId,
            editingMode,
            fieldId: optsVariable.nodeId,
            update: { text: value?.optionText || optsVariable.label },
          });
          if (errMessage)
            dispatch({
              payload: {
                key: uuid4(),
                message: errMessage,
                variant: 'error',
              },
              type: NotificationsActionType.PUSH_NOTIFICATION,
            });
          break;
        }
        case 'StaticFileVersion': {
          const isInPDF = getIsPDFFieldInDoc(optsVariable.nodeId);
          if (!isInPDF) {
            await updateVariable({
              variables: {
                input: { id: optsVariable.id, props: { selectedAnswerId: value?.id ?? null } },
              },
            });
            return;
          }

          // Syncing annotations to DB updates field in DB.
          setPDFFieldValue(optsVariable.nodeId, value?.optionText ?? '');
          break;
        }
      }
    },
  };
}

export function useUpdateCheckboxVariableValue(checkboxVariable: PanelVariableType): {
  isInChildren: boolean;
  onClickVariable: () => void;
  options: OptionsValue[];
  resetOnBlur: () => OpCode;
  selectedAnswerIds: string[];
  updateCheckboxVariable: (
    selectedAnswerIds: string[],
    options?: { skipped?: boolean }
  ) => Promise<void>;
} {
  const setVariablePanelState = useSetVariablePanelState();
  const { isInChildren } = useIsFieldInDocJSON(checkboxVariable);
  const { resetOnBlur } = useResetTemplateFieldOnBlur(checkboxVariable);
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { setPDFCheckboxSelectionFromPanel } = usePDFFieldAPI(documentViewer, tabId);
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { document } = useGetCurrentDoc();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const isContract = document?.variant === Apollo.DocumentVariant.Document;
  const serverIds = getSelectedIdsFromAnswers(checkboxVariable.selectedAnswers ?? null);
  const [pendingDraft, setPendingDraft] = useState<string[] | null>(null);
  const selectedAnswerIdsRef = useRef(serverIds);
  // Ignore stale mutation results when the user toggles faster than requests resolve.
  const updateRequestGenerationRef = useRef(0);
  const optionsList = checkboxVariable.answerOptions?.options ?? [];

  const selectedAnswerIds =
    pendingDraft !== null && !areSameSelectedAnswerIds(pendingDraft, serverIds)
      ? pendingDraft
      : serverIds;
  selectedAnswerIdsRef.current = selectedAnswerIds;

  async function updateCheckboxVariable(
    nextIds: string[],
    { skipped }: { skipped?: boolean } = {}
  ) {
    const previousIds = selectedAnswerIdsRef.current;
    const requestGeneration = ++updateRequestGenerationRef.current;
    const canSyncEditor =
      document?.latestVersion.__typename === 'DocumentVersion' &&
      editor &&
      currentUserId &&
      editingMode;
    const assigneeId = checkboxVariable.action?.assignee?.id ?? null;
    function syncEditorSelection(ids: string[]) {
      if (!canSyncEditor) return;
      suspendSelectionScroll();
      /**
       * Only the checked state changes here. Passing `answerOptionsInput` would reconcile the
       * whole option set against the panel's list, dropping or relabeling options the panel
       * has not caught up on yet, so pass just the ids needed to rename older nodes.
       */
      AuthorizedTransforms.updateInputField(editor, {
        assigneeId: isContract ? assigneeId : null,
        authorId: currentUserId,
        editingMode,
        fieldId: checkboxVariable.nodeId,
        legacyOptionIds: getCheckboxOptionIdentities(optionsList),
        update: { checkboxSelectedAnswerIds: toCheckboxChoiceRefIds(ids, optionsList) },
      });
    }

    async function syncPDFSelection(ids: string[]) {
      if (
        document?.latestVersion.__typename !== 'StaticFileVersion' ||
        !getIsPDFFieldInDoc(checkboxVariable.nodeId)
      ) {
        return;
      }
      await setPDFCheckboxSelectionFromPanel({
        optionsList,
        selectedAnswerIds: ids,
        variableNodeId: checkboxVariable.nodeId,
      });
    }

    syncEditorSelection(nextIds);
    void syncPDFSelection(nextIds);
    setPendingDraft(nextIds);
    selectedAnswerIdsRef.current = nextIds;

    const skippedProp =
      skipped !== undefined ? { skipped } : nextIds.length > 0 ? { skipped: false } : {};

    const { data } = await updateVariable({
      variables: {
        input: {
          id: checkboxVariable.id,
          props: {
            selectedAnswerIds: nextIds,
            ...skippedProp,
          },
        },
      },
    });

    if (!data?.updateVariable) {
      // Only roll back if no newer toggle started after this request.
      if (updateRequestGenerationRef.current !== requestGeneration) return;

      selectedAnswerIdsRef.current = previousIds;
      setPendingDraft(previousIds);
      syncEditorSelection(previousIds);
      void syncPDFSelection(previousIds);
      return;
    }

    if (updateRequestGenerationRef.current === requestGeneration) {
      setPendingDraft(null);
    }
  }

  return {
    isInChildren,
    onClickVariable: () => setVariablePanelState({ focusedField: checkboxVariable }),
    options: optionsList,
    resetOnBlur,
    selectedAnswerIds,
    updateCheckboxVariable,
  };
}

export function useScrollFieldIntoView(): {
  scrollFieldIntoView: (field: Pick<Apollo.Field, 'nodeId' | '__typename'>) => void;
} {
  const editor = useMaybeEditor();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const rWHeightPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.windowHeight
  );
  const rWOffsetPx = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.reactWindow.scrollOffset
  );
  const { document } = useGetCurrentDoc();
  const documentViewer = useMaybeDocumentViewer();
  const pdfFieldsByNodeId = useGetPDFFieldsByNodeId(tabId);
  const scrollFieldIntoView = useCallback(
    (field: Pick<Apollo.Field, 'nodeId' | '__typename'>): void => {
      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion': {
          if (!editor) return;

          const selectedFieldEntry = Editor.above(editor, { match: isInputFieldElement });
          const isFieldSelected =
            selectedFieldEntry && selectedFieldEntry[0].fieldId === field.nodeId;

          if (!isFieldSelected) {
            ClientTransforms.navigateToNextDynamicElement(editor, {
              match: n => n.fieldId === field.nodeId,
              rWHeightPx,
              rWOffsetPx,
              /**
               * Mobile-only overrides:
               * - `at`: sheet inputs own focus, so `editor.selection` is
               *   typically `null` when Next fires and the transform would
               *   bail. Anchor from the document start so the field is always
               *   found by `nodeId`.
               * - `scrollAlignment: 'top'`: react-window's viewport spans the
               *   full window on mobile, so the default "already on-screen"
               *   check leaves the field hidden behind the bottom sheet.
               *   Forcing top places it above the sheet.
               */
              ...(IS_MOBILE && {
                at: Editor.start(editor, []),
                scrollAlignment: 'top',
              }),
            });
          }

          break;
        }
        case 'StaticFileVersion': {
          if (!documentViewer) return;

          const pdfField = pdfFieldsByNodeId.get(field.nodeId);
          const [foundAnnotation] = [...(pdfField?.widgets ?? [])].sort(
            comparePDFAnnotationPosition
          );
          if (foundAnnotation) {
            documentViewer.displayPageLocation(
              foundAnnotation.getPageNumber(),
              foundAnnotation.X,
              foundAnnotation.Y,
              true
            );
          }
        }
      }
    },
    [
      document?.latestVersion.__typename,
      documentViewer,
      editor,
      rWHeightPx,
      rWOffsetPx,
      pdfFieldsByNodeId,
    ]
  );

  return { scrollFieldIntoView };
}

export function useResetTemplateFieldOnBlur(variable: UseUpdateVariable): {
  resetOnBlur: () => OpCode;
} {
  const editor = useMaybeEditor();
  const { document } = useGetCurrentDoc();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { setPDFFieldValue } = usePDFFieldAPI(documentViewer, tabId);
  const { isInChildren } = useIsFieldInDocJSON(variable);
  const { getIsPDFFieldInDoc } = useGetIsPDFFieldInDocument(tabId);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const isContract = document?.variant === Apollo.DocumentVariant.Document;
  const resetOnBlur = useCallback((): OpCode => {
    // Don't reset value to variable.label if it's a contract.  Only for templates.
    if (isContract) return OpCode.Noop;

    switch (document?.latestVersion.__typename) {
      case 'DocumentVersion': {
        if (!isInChildren || !currentUserId || !editingMode || !editor) return OpCode.Noop;

        AuthorizedTransforms.updateInputField(editor, {
          // Pass null to skip assignee checks.
          assigneeId: null,
          authorId: currentUserId,
          editingMode,
          fieldId: variable.nodeId,
          update: { text: variable.label },
        });
        return OpCode.Success;
      }
      case 'StaticFileVersion': {
        const isInPDF = getIsPDFFieldInDoc(variable.nodeId);
        if (!isInPDF) return OpCode.Noop;

        setPDFFieldValue(variable.nodeId, '');
        return OpCode.Success;
      }
      default:
        return OpCode.Noop;
    }
  }, [
    AuthorizedTransforms,
    currentUserId,
    document?.latestVersion.__typename,
    editingMode,
    editor,
    getIsPDFFieldInDoc,
    isContract,
    isInChildren,
    setPDFFieldValue,
    variable.label,
    variable.nodeId,
  ]);

  return {
    resetOnBlur,
  };
}

export function useCheckboxOptionLabelDbSync(options: {
  documentId: string;
  editor: AlineClientEditor | null;
  editorOnChangeCounter: number;
}): void {
  const { documentId, editor, editorOnChangeCounter } = options;
  const { variables } = useGetVariablesForDocument(documentId);
  const { fieldIdsInChildren } = useGetFieldsInJSON();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const editingDisabled = useEditingDisabled();
  const [updateVariable] = Apollo.useUpdateDocumentVariableMutation();

  const syncPendingLabels = useEffectEvent(async () => {
    if (
      !editor ||
      !currentUserId ||
      !editingMode ||
      editor.userState?.isReadOnly ||
      editingDisabled
    )
      return;

    const updateContext = EDITOR_TO_UPDATE_CONTEXT.get(editor);
    const pendingKeys = updateContext?.pendingCheckboxOptionLabelKeys;
    if (!pendingKeys?.size) return;

    const keysSnapshot = [...pendingKeys];
    const checkboxVariableNodeIdByOptionId = new Map<string, string>();
    for (const variable of variables) {
      if (variable.variant !== Apollo.MetadataVariant.Checkbox) continue;
      for (const option of variable.answerOptions?.options ?? []) {
        checkboxVariableNodeIdByOptionId.set(option.id, variable.nodeId);
      }
    }

    const fieldIdsToSync = new Set<string>();
    for (const optionId of keysSnapshot) {
      const variableNodeId = checkboxVariableNodeIdByOptionId.get(optionId);
      if (variableNodeId) fieldIdsToSync.add(variableNodeId);
    }

    function removeSyncedKeysForVariable(variableNodeId: string) {
      const optionIds = new Set(
        variables
          .find(variable => variable.nodeId === variableNodeId)
          ?.answerOptions?.options?.map(option => option.id) ?? []
      );
      for (const key of keysSnapshot) {
        if (optionIds.has(key)) pendingKeys?.delete(key);
      }
    }

    for (const variable of variables) {
      if (
        !fieldIdsToSync.has(variable.nodeId) ||
        variable.variant !== Apollo.MetadataVariant.Checkbox ||
        !variable.answerOptions ||
        !fieldIdsInChildren.has(variable.nodeId)
      )
        continue;

      const labelsByOptionId = Queries.getCheckboxFieldOptionTextByOptionId(
        editor,
        variable.nodeId
      );
      const optionRows = variable.answerOptions.options;

      let syncEditor = false;
      const nextOptions = optionRows.map(option => {
        // Nodes are keyed by choiceRefId, except in documents still holding the row id.
        const list = labelsByOptionId.get(option.choiceRefId) ?? labelsByOptionId.get(option.id);
        if (!list?.length) return option;

        const normalized = list.map(label => label.trim());
        const optionText = Queries.pickCanonicalCheckboxOptionLabel(normalized, option.optionText);
        if (optionText !== option.optionText || normalized.some(text => text !== optionText)) {
          syncEditor = true;
        }
        return { ...option, optionText };
      });

      const needsVariablePersist = nextOptions.some(
        (nextOption, i) => nextOption.optionText !== optionRows[i]?.optionText
      );

      if (!needsVariablePersist && !syncEditor) {
        removeSyncedKeysForVariable(variable.nodeId);
        continue;
      }

      if (needsVariablePersist) {
        const { data } = await updateVariable({
          variables: {
            input: {
              id: variable.id,
              upsertAnswerOptions: {
                id: variable.answerOptions.id,
                options: nextOptions.map(o => ({
                  id: o.id,
                  optionText: o.optionText,
                  triggerAction: o.triggerAction,
                })),
              },
            },
          },
        });
        if (!data?.updateVariable) {
          removeSyncedKeysForVariable(variable.nodeId);
          continue;
        }
      }

      AuthorizedTransforms.updateInputField(editor, {
        answerOptionsInput: nextOptions.map(o => ({
          id: o.choiceRefId,
          legacyId: o.id,
          optionText: o.optionText,
        })),
        assigneeId: null,
        authorId: currentUserId,
        editingMode,
        fieldId: variable.nodeId,
        update: {},
      });

      removeSyncedKeysForVariable(variable.nodeId);
    }
  });

  const [debouncedSync] = useState(() => debounce((callback: () => void) => callback(), 500));

  useEffect(() => {
    if (!editor) return;
    const pendingKeys = EDITOR_TO_UPDATE_CONTEXT.get(editor)?.pendingCheckboxOptionLabelKeys;
    if (!pendingKeys?.size) return;

    debouncedSync(() => void syncPendingLabels());

    return () => debouncedSync.clear();
  }, [debouncedSync, editor, editorOnChangeCounter]);
}

function getSelectedIdsFromAnswers(
  selectedAnswers: PanelVariableType['selectedAnswers']
): string[] {
  if (!selectedAnswers?.length) return [];
  return selectedAnswers.map(a => a.id);
}

function areSameSelectedAnswerIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every(id => bSet.has(id));
}
